"use client";

import { useEffect, useRef, useState } from "react";
import { buttonClass } from "@/components/ui/button";
import { IconFilter } from "@/components/ui/icons";
import { Sheet } from "@/components/ui/sheet";
import { useLocale } from "@/components/layout/locale-context";
import { plural } from "@/i18n/config";
import type { Facets, TourQuery } from "../filters";
import type { ListingNav } from "../listing-url";
import { FilterForm, type FilterTexts } from "./filter-form";

// Mobil filtre paneli: alttan açılan sayfa (Sheet, yerel <dialog>). Seçim yaptıkça sonuç sayısı canlı güncellenir
// (/api/tur-sayisi, 250 ms gecikmeyle) ve alt çubuktaki düğme "N turu göster" olur; sayı ekran okuyucuya kibarca bildirilir.
// "Temizle" yalnızca paneli sıfırlar (henüz uygulamaz). Düğme formun dışında: <button form="…">.
export function MobileFilters({
  nav,
  t,
  query,
  facets,
  activeCount,
  total,
}: {
  nav: ListingNav;
  t: FilterTexts;
  query: TourQuery;
  facets: Facets;
  activeCount: number;
  /** Şu anki (uygulanmış filtrelerle) sonuç sayısı */
  total: number;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const locale = useLocale();
  const [count, setCount] = useState<number | null>(total);
  const [loading, setLoading] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controller = useRef<AbortController | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    controller.current?.abort();
  }, []);

  const recount = (qs: string) => {
    if (timer.current) clearTimeout(timer.current);
    setLoading(true);
    timer.current = setTimeout(async () => {
      controller.current?.abort();
      const ctrl = new AbortController();
      controller.current = ctrl;
      try {
        const res = await fetch(`/api/tur-sayisi?dil=${locale}${qs ? `&${qs}` : ""}`, { signal: ctrl.signal });
        const data = (await res.json()) as { total: number | null };
        setCount(typeof data.total === "number" ? data.total : null);
      } catch {
        if (!ctrl.signal.aborted) setCount(null);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 250);
  };

  const label = count === null ? t.showResults : plural(count, t.showCount);

  return (
    <div className="lg:hidden">
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={() => {
          setCount(total);
          dialogRef.current?.showModal();
        }}
        className={buttonClass("secondary", "sm")}
      >
        <IconFilter size={18} /> {t.title}
        {activeCount > 0 && (
          <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-cta px-1.5 text-xs font-bold text-on-cta">
            {activeCount}
            <span className="sr-only"> {t.activeCount}</span>
          </span>
        )}
      </button>
      <Sheet
        dialogRef={dialogRef}
        titleId="mobile-filters-title"
        title={t.title}
        closeLabel={t.close}
        footer={
          <div className="flex items-center gap-3">
            <button
              type="button"
              className={buttonClass("ghost", "md", "shrink-0")}
              onClick={() => {
                setFormKey((k) => k + 1);
                recount(query.sirala !== "tarih" ? `sirala=${query.sirala}` : "");
              }}
            >
              {t.reset}
            </button>
            <button type="submit" form="mobile-filter-form" aria-busy={loading || undefined} className={buttonClass("cta", "md", "flex-1")}>
              {label}
            </button>
            <p aria-live="polite" className="sr-only">
              {loading || count === null ? "" : plural(count, t.liveCount)}
            </p>
          </div>
        }
      >
        <div className="px-4 py-5">
          <FilterForm
            key={formKey}
            formId="mobile-filter-form"
            externalSubmit
            nav={nav}
            t={t}
            query={formKey === 0 ? query : { sirala: query.sirala, sayfa: 1 }}
            facets={facets}
            idPrefix="m"
            autoSubmit={false}
            onChangeParams={recount}
            onSubmitted={() => dialogRef.current?.close()}
          />
        </div>
      </Sheet>
    </div>
  );
}
